import type { ChangeEvent, KeyboardEvent, Ref } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { InputTextProperties } from "../input-text";
import { InputText } from "../input-text";
import {
  CalendarDate,
  getLocalTimeZone,
  maxDate,
  minDate,
} from "@internationalized/date";
import { useMergeRefs } from "@floating-ui/react";
import { parse_date } from "@jetblue/date-parser";

type DateInputProperties = {
  className?: string | undefined;
  closeCalendar?: () => void;
  /** Indicates whether the calendar popover is currently open. When false, default Enter key behavior (e.g. form submission) will not be prevented. Defaults to true for backward compatibility. */
  isCalendarOpen?: boolean | undefined;
  error?: InputTextProperties["error"] | undefined;
  inputClassName?: string | undefined;
  isRequired?: boolean | undefined;
  label: string;
  max?: CalendarDate;
  min?: CalendarDate;
  name?: string | undefined;
  onBlur?: (() => void) | undefined;
  onChange?: (date?: CalendarDate, changeFocus?: boolean) => void;
  onFocus?: (() => void) | undefined;
  onFocusChange?: ((focused: boolean) => void) | undefined;
  readOnly?: boolean;
  ref?: Ref<HTMLInputElement>;
  showValidation?: boolean | undefined;
  swapFocus?: (() => void) | undefined;
  tabIndex?: number | undefined;
  value: CalendarDate | undefined;
};

const defaultDateFormat = new Intl.DateTimeFormat("en-us", {
  day: "numeric",
  month: "short",
  weekday: "short",
});

export const longDateFormat = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "long",
  year: "numeric",
});
/**
 * Converts a CalendarDate to a formatted string (e.g., "Mon, Jan 1").
 *
 * @param date - The date to format.
 * @returns The formatted date string, or an empty string if no date is provided.
 */
export const toDateString = (
  date?: CalendarDate,
  dateFormat = defaultDateFormat,
) => (date ? dateFormat.format(date.toDate(getLocalTimeZone())) : "");
export const DateInput = ({
  className,
  error,
  inputClassName,
  isRequired,
  isCalendarOpen,
  label,
  max,
  min,
  onChange,
  onFocus,
  onFocusChange,
  readOnly,
  ref,
  showValidation,
  tabIndex,
  value,
  closeCalendar,
  ...rest
}: DateInputProperties) => {
  const [pristine, setPristine] = useState<boolean>(true);
  const [text, setText] = useState(toDateString(value));
  // Treat undefined as true to preserve existing behavior unless explicitly set to false
  const calendarIsOpen = isCalendarOpen ?? true;

  const localReference = useRef<HTMLInputElement>(null);
  const mergedReferences = useMergeRefs([localReference, ref]);
  const selectAllNextRender = useRef(false);

  useLayoutEffect(() => {
    if (selectAllNextRender.current) {
      requestAnimationFrame(() => {
        localReference.current?.select();
      });
      selectAllNextRender.current = false;
    }
  }, [text]);

  const tryCommit = () => {
    if (!text) {
      commitDate();
      return;
    }
    const parsed = parse_date(text);
    if (parsed) {
      commitDate(
        new CalendarDate(
          parsed.getFullYear(),
          parsed.getMonth() + 1,
          parsed.getDate(),
        ),
      );
    } else {
      commitDate(value);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newText = event.target.value;

    if (newText) {
      setPristine(false);
    }
    setText(newText);
  };

  const commitDate = (date?: CalendarDate) => {
    const clampedDate = date && minDate(maxDate(min, date), max);
    if (clampedDate) {
      onChange?.(clampedDate);
      setText(toDateString(clampedDate));
    } else {
      onChange?.();
      setText("");
    }
    setPristine(true);
  };
  const handleBlur = () => {
    if (!pristine) {
      tryCommit();
    }
  };
  const handleFocus = (focused: boolean) => {
    onFocusChange?.(focused);
    if (focused) {
      localReference.current?.select();
    }
  };

  const handleArrowKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!value) {
      commitDate(min);
      selectAllNextRender.current = true;
      return;
    }
    let next = value;
    switch (event.key) {
      case "ArrowLeft": {
        next = next.add({ days: -1 });
        break;
      }
      case "ArrowRight": {
        next = next.add({ days: 1 });
        break;
      }
      case "ArrowUp": {
        next = next.add({ days: -7 });
        break;
      }
      case "ArrowDown": {
        next = next.add({ days: 7 });
        break;
      }
      default: {
        return;
      }
    }

    event.preventDefault();
    selectAllNextRender.current = true;
    commitDate(next);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (pristine) {
      switch (event.key) {
        case "ArrowLeft":
        case "ArrowRight":
        case "ArrowUp":
        case "ArrowDown": {
          handleArrowKey(event);
          return;
        }
        case "Escape": {
          closeCalendar?.();
          return;
        }
        case "Enter": {
          if (calendarIsOpen) {
            event.preventDefault();
            closeCalendar?.();
          }
          return;
        }
        default: {
          return; // ignore other keys while pristine
        }
      }
    }

    switch (event.key) {
      case "Escape": {
        setText(toDateString(value));
        setPristine(true);
        closeCalendar?.();
        return;
      }
      case "Enter": {
        const parsed = parse_date(text);
        if (parsed) {
          commitDate(
            new CalendarDate(
              parsed.getFullYear(),
              parsed.getMonth() + 1,
              parsed.getDate(),
            ),
          );
        } else if (text) {
          setText("");
        }
        if (calendarIsOpen) {
          closeCalendar?.();
        }
        return;
      }
      default: {
        return; // ignore other keys when dirty
      }
    }
  };

  useEffect(() => {
    setText(toDateString(value));
  }, [value]);

  return (
    <InputText
      autoComplete="off"
      className={className}
      error={error}
      inputClassName={inputClassName}
      inputProperties={{
        readOnly,
        ref: mergedReferences,
        tabIndex,
      }}
      isInvalid={showValidation ? !value : false}
      isRequired={isRequired}
      label={label}
      onBlur={handleBlur}
      onChange={handleChange}
      onFocus={onFocus}
      onFocusChange={handleFocus}
      onKeyDown={handleKeyDown}
      type="text"
      value={text}
      {...rest}
    />
  );
};

DateInput.displayName = "DatePicker.Input";
